/**
 * Shopify Chat Hook v2 - Uses Temporal + Redis SSE for streaming.
 *
 * Supports session-based chat - messages are scoped to a specific session
 * within a connection.
 *
 * Message loading is IMPERATIVE (call loadMessages explicitly) rather than
 * reactive (useEffect watching sessionId). This gives clearer control flow
 * and avoids closure/stale state issues.
 *
 * Tools are stored in message.tools (persisted in DB), not separate state.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  sendMessage as sendMessageApi,
  getMessages,
  getMessage,
  connectToStream,
  parseStreamEvent,
  TOOL_DISPLAY_NAMES,
  type ShopifyChatMessage,
  type ToolCall,
} from "~/lib/network/shopify-chat";

interface UseShopifyChatOptions {
  connectionId: string;
  sessionId: string | null;
}

interface UseShopifyChatReturn {
  messages: ShopifyChatMessage[];
  streamingContent: string;
  isLoading: boolean;
  isStreaming: boolean;
  error: Error | null;
  sendMessage: (text: string, sessionIdOverride?: string) => Promise<void>;
  loadMessages: (targetSessionId: string) => Promise<void>;
}

export function useShopifyChat({
  connectionId,
  sessionId,
}: UseShopifyChatOptions): UseShopifyChatReturn {
  const [messages, setMessages] = useState<ShopifyChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Track current streaming message ID for cleanup
  const streamingMessageIdRef = useRef<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  // Track content length for tool position (refs avoid stale closures)
  const contentLengthRef = useRef(0);

  // Helper to update the last assistant message's tools
  const updateLastMessageTools = useCallback((updater: (tools: ToolCall[]) => ToolCall[]) => {
    setMessages((prev) => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (lastIdx >= 0 && updated[lastIdx].role === "assistant") {
        updated[lastIdx] = {
          ...updated[lastIdx],
          tools: updater(updated[lastIdx].tools || []),
        };
      }
      return updated;
    });
  }, []);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  // Clear state when session is deselected (null)
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setStreamingContent("");
      setIsStreaming(false);
      setError(null);
      // Close any active stream
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      streamingMessageIdRef.current = null;
      contentLengthRef.current = 0;
    }
  }, [sessionId]);

  // Handle stream events (shared between reconnect and sendMessage)
  const handleStreamEvent = useCallback(
    (evt: { type: string; data: string }) => {
      if (evt.type === "token") {
        setStreamingContent((prev) => {
          const newContent = prev + evt.data;
          contentLengthRef.current = newContent.length;
          return newContent;
        });
      } else if (evt.type === "tool_start") {
        const displayName = TOOL_DISPLAY_NAMES[evt.data] || evt.data;
        const position = contentLengthRef.current;
        const newTool: ToolCall = {
          name: evt.data,
          display_name: displayName,
          status: "running",
          content_position: position,
        };
        updateLastMessageTools((tools) => [...tools, newTool]);
      } else if (evt.type === "tool_end") {
        updateLastMessageTools((tools) => {
          const updated = [...tools];
          // Find the last running tool with this name and mark it complete
          for (let i = updated.length - 1; i >= 0; i--) {
            if (updated[i].name === evt.data && updated[i].status === "running") {
              updated[i] = { ...updated[i], status: "completed" };
              break;
            }
          }
          return updated;
        });
      }
    },
    [updateLastMessageTools]
  );

  const handleStreamEnd = useCallback(
    async (messageId: string) => {
      setIsStreaming(false);
      streamingMessageIdRef.current = null;
      eventSourceRef.current = null;

      // Fetch the final message to get artifacts and tools from DB
      try {
        const finalMessage = await getMessage(connectionId, messageId);

        setMessages((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex((m) => m._id === messageId);
          if (idx !== -1) {
            updated[idx] = finalMessage;
          }
          return updated;
        });
      } catch (err) {
        console.error("Failed to fetch final message:", err);
      }

      setStreamingContent("");
      contentLengthRef.current = 0;
    },
    [connectionId]
  );

  // Reconnect to an in-progress stream (e.g., page refresh during streaming)
  // Backend sends ALL tokens from stream start on connect. We rebuild from
  // scratch (like Gamgee) rather than deduplicating - simpler and no edge cases.
  const reconnectToStream = useCallback(
    (targetSessionId: string, messageId: string) => {
      setIsStreaming(true);
      streamingMessageIdRef.current = messageId;
      setStreamingContent(""); // Start fresh - backend replays all tokens
      contentLengthRef.current = 0;

      // Clear tools on the streaming message (will be rebuilt from events)
      updateLastMessageTools(() => []);

      const eventSource = connectToStream(connectionId, targetSessionId, messageId);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        const events = parseStreamEvent(event.data);

        for (const evt of events) {
          if (evt.type === "system" && evt.data === "session_end") {
            handleStreamEnd(messageId);
            eventSource.close();
            return;
          }
          handleStreamEvent(evt);
        }
      };

      eventSource.onerror = () => {
        handleStreamEnd(messageId);
        eventSource.close();
      };
    },
    [connectionId, handleStreamEvent, handleStreamEnd, updateLastMessageTools]
  );

  // Load messages for a session - call this explicitly when switching sessions
  const loadMessages = useCallback(
    async (targetSessionId: string) => {
      if (!targetSessionId) return;

      try {
        setIsLoading(true);
        setError(null);
        const msgs = await getMessages(connectionId, targetSessionId);
        setMessages(msgs);

        // Check if the last message is still streaming (e.g., page refresh)
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg?.role === "assistant" && lastMsg.is_streaming) {
          if (!eventSourceRef.current && streamingMessageIdRef.current !== lastMsg._id) {
            reconnectToStream(targetSessionId, lastMsg._id);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to load messages"));
      } finally {
        setIsLoading(false);
      }
    },
    [connectionId, reconnectToStream]
  );

  const sendMessage = useCallback(
    async (text: string, sessionIdOverride?: string) => {
      const effectiveSessionId = sessionIdOverride || sessionId;
      if (!text.trim() || isStreaming || !effectiveSessionId) return;

      try {
        setError(null);

        // Call API to send message
        const { input_message_id, output_message_id } = await sendMessageApi(
          connectionId,
          effectiveSessionId,
          text
        );

        // Add user message to state immediately
        const userMessage: ShopifyChatMessage = {
          _id: input_message_id,
          connection_id: connectionId,
          session_id: effectiveSessionId,
          role: "user",
          content: text,
          artifacts: [],
          tools: [],
          is_streaming: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Add placeholder assistant message
        const assistantMessage: ShopifyChatMessage = {
          _id: output_message_id,
          connection_id: connectionId,
          session_id: effectiveSessionId,
          role: "assistant",
          content: "",
          artifacts: [],
          tools: [],
          is_streaming: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setIsStreaming(true);
        setStreamingContent("");
        contentLengthRef.current = 0;
        streamingMessageIdRef.current = output_message_id;

        // Connect to SSE stream
        const eventSource = connectToStream(connectionId, effectiveSessionId, output_message_id);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          const events = parseStreamEvent(event.data);

          for (const evt of events) {
            if (evt.type === "system" && evt.data === "session_end") {
              handleStreamEnd(output_message_id);
              eventSource.close();
              return;
            }
            handleStreamEvent(evt);
          }
        };

        eventSource.onerror = () => {
          handleStreamEnd(output_message_id);
          eventSource.close();
        };
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to send message"));
        setIsStreaming(false);
      }
    },
    [connectionId, sessionId, isStreaming, handleStreamEvent, handleStreamEnd]
  );

  return {
    messages,
    streamingContent,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    loadMessages,
  };
}
